import { useEffect, useRef, useState } from "react";
import { X, ChevronLeft, ChevronRight, Play } from "lucide-react";

/**
 * Full-page media explorer.
 * `items` = list of { type, src, embed, thumb } from buildMediaList().
 *
 * The explorer accepts an optional `startIndex`. When set, the explorer jumps
 * straight into the zoomed view of that item (videos begin playing
 * immediately instead of forcing a second click). The grid stays available
 * via the "All media" link in the top bar.
 *
 * Close UX:
 *   - ESC always closes the explorer entirely (even from zoom mode).
 *   - A prominent top-right Close button is pinned to both views.
 *   - Tapping the dark backdrop (outside the media element) in zoom mode
 *     also closes the explorer.
 */
export default function PhotoExplorer({ open, onClose, items, propertyName, startIndex = null }) {
  const media = items || [];
  const [idx, setIdx] = useState(startIndex);
  const lastStartRef = useRef(startIndex);

  // When the explorer is opened with a fresh startIndex, respect it. Using
  // an explicit ref avoids the common bug where the prop lands once on mount
  // and subsequent opens reuse the stale state.
  useEffect(() => {
    if (!open) return;
    if (startIndex !== lastStartRef.current) {
      lastStartRef.current = startIndex;
      setIdx(startIndex);
    } else if (startIndex !== null && idx === null) {
      setIdx(startIndex);
    }
  }, [open, startIndex, idx]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      // ESC always closes the explorer outright — no modal-inside-modal dance.
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight" && idx !== null) setIdx((i) => (i + 1) % media.length);
      if (e.key === "ArrowLeft" && idx !== null) setIdx((i) => (i - 1 + media.length) % media.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, idx, media.length, onClose]);

  useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  if (!open) return null;

  const current = idx != null ? media[idx] : null;

  return (
    <div role="dialog" aria-modal="true" data-testid="photo-explorer" className="fixed inset-0 z-[100] flex flex-col bg-parchment">
      <div className="sticky top-0 z-20 flex items-center justify-between border-b border-forest/10 bg-parchment/95 px-4 py-3 backdrop-blur sm:px-6">
        <div className="flex items-center gap-3">
          <button onClick={onClose} data-testid="close-explorer" aria-label="Close media viewer" className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-forest/20 bg-white text-forest hover:bg-mist">
            <X size={18} />
          </button>
          <div>
            <p className="text-[11px] uppercase tracking-wider text-amber">Tour</p>
            <p className="font-display text-base text-forest sm:text-lg">{propertyName}</p>
          </div>
        </div>
        {idx !== null ? (
          <button
            onClick={() => setIdx(null)}
            data-testid="back-to-grid"
            className="inline-flex items-center gap-1 rounded-full border border-forest/20 bg-white px-3 py-1.5 text-sm text-forest hover:bg-mist"
          >
            All media
          </button>
        ) : (
          <span className="text-xs text-noir/60">{media.length} items</span>
        )}
      </div>

      {idx === null ? (
        <div className="mx-auto w-full max-w-6xl flex-1 overflow-y-auto px-4 py-8 sm:px-6">
          <h3 className="font-display text-2xl">All media</h3>
          <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-3">
            {media.map((m, i) => (
              <button
                key={m.src || m.embed || `m-${i}`}
                onClick={() => setIdx(i)}
                data-testid={`explorer-media-${i}`}
                data-media-type={m.type}
                className={`group relative overflow-hidden rounded-lg ${i % 7 === 0 ? "col-span-2 row-span-2 aspect-[4/3]" : "aspect-[4/3]"}`}
              >
                {m.thumb ? (
                  <img src={m.thumb} alt="" loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                ) : m.provider === "file" ? (
                  <video src={m.src} preload="metadata" muted playsInline className="h-full w-full object-cover" />
                ) : (
                  <div className="h-full w-full bg-noir/80" />
                )}
                {m.type === "video" && (
                  <>
                    <span className="absolute inset-0 flex items-center justify-center bg-black/20">
                      <span className="flex h-12 w-12 items-center justify-center rounded-full bg-parchment/95 text-forest">
                        <Play size={18} className="translate-x-0.5 fill-current" />
                      </span>
                    </span>
                    <span className="absolute left-2 top-2 rounded-full bg-black/60 px-2 py-0.5 text-[10px] uppercase tracking-wider text-parchment">Video</span>
                  </>
                )}
              </button>
            ))}
          </div>
        </div>
      ) : (
        // Backdrop click closes the explorer. We attach the handler to the
        // wrapper and stop propagation on the inner media element so taps on
        // the photo/video themselves don't accidentally dismiss.
        <div className="relative flex flex-1 items-center justify-center bg-noir/95 px-4" onClick={onClose} data-testid="zoom-backdrop">
          <button onClick={(e) => { e.stopPropagation(); setIdx((i) => (i - 1 + media.length) % media.length); }} className="absolute left-4 z-20 p-3 text-parchment hover:text-gold" aria-label="Previous"><ChevronLeft size={36} /></button>
          {current.type === "image" ? (
            <img src={current.src} alt="" onClick={(e) => e.stopPropagation()} className="max-h-[85vh] max-w-full rounded-lg object-contain" />
          ) : current.provider === "file" ? (
            <video
              key={current.src}
              src={current.src}
              controls
              autoPlay
              playsInline
              onClick={(e) => e.stopPropagation()}
              className="max-h-[85vh] max-w-full rounded-lg"
            />
          ) : (
            <iframe
              key={current.embed}
              title={`${propertyName} video`}
              src={`${current.embed}${current.embed.includes("?") ? "&" : "?"}autoplay=1&playsinline=1`}
              onClick={(e) => e.stopPropagation()}
              className="aspect-video w-full max-w-5xl rounded-lg"
              allow="autoplay; encrypted-media; picture-in-picture"
              allowFullScreen
            />
          )}
          <button onClick={(e) => { e.stopPropagation(); setIdx((i) => (i + 1) % media.length); }} className="absolute right-4 z-20 p-3 text-parchment hover:text-gold" aria-label="Next"><ChevronRight size={36} /></button>
          <span className="pointer-events-none absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full bg-black/50 px-3 py-1 text-xs text-parchment">{idx + 1} / {media.length}</span>
        </div>
      )}
    </div>
  );
}

